import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import "./casino/CasinoPage.css";
import GameCard from "./components/GameCard";
import { FaSearch, FaHome, FaGamepad, FaDice, FaTrophy, FaChevronLeft, FaChevronRight, FaChevronDown, FaCheck } from "react-icons/fa";
import { MdLiveTv, MdCasino, MdSportsSoccer } from "react-icons/md";
import { GiCardRandom, GiPokerHand, GiRollingDices } from "react-icons/gi";
import api from "../../utils/api";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

const getIconForType = (typeId) => {
    const id = typeId.toLowerCase();
    if (id === 'all') return <FaHome />;
    if (id.includes('live')) return <MdLiveTv />;
    if (id.includes('slot')) return <MdCasino />;
    if (id.includes('card') || id.includes('poker')) return <GiPokerHand />;
    if (id.includes('table')) return <GiCardRandom />;
    if (id.includes('virtual') || id.includes('sport')) return <MdSportsSoccer />;
    if (id.includes('sic') || id.includes('dice')) return <FaDice />;
    if (id.includes('crash')) return <FaGamepad />;
    if (id.includes('lottery')) return <GiRollingDices />;
    return <FaTrophy />;
};

const GamePageTemplate = ({
    pageTitle,
    bannerTitle, // e.g. "Live Casino"
    bannerImage,
    fetchParams = {}, // e.g. { isLiveGame: true } or { gameType: 'crash' }
    showGameTypeFilter = true, // Whether to show the navigation slider for game types
    fixedGameType = null, // If set, forces this gameType and ignores activeNav logic for gameType
    excludeGameTypes = "",
    excludeGameType = null // Single exclusion
}) => {
    const user = useSelector((state) => state.user);
    const navigate = useNavigate();

    // Scroll to top immediately on mount
    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // State
    const [activeNav, setActiveNav] = useState("all");
    const [selectedProviders, setSelectedProviders] = useState([]);
    const [isProviderDropdownOpen, setIsProviderDropdownOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchTerm, setSearchTerm] = useState("");

    // Pagination State
    const [games, setGames] = useState([]);
    const [providers, setProviders] = useState([]);
    const [gameTypes, setGameTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [nextCursor, setNextCursor] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);

    // Refs
    // Refs
    const navRef = useRef(null);
    const providerRef = useRef(null);
    const providerScrollRef = useRef(null); // New ref for top slider
    const gamesContainerRef = useRef(null);
    const observer = useRef();

    // Debounce Search
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchQuery(searchTerm);
        }, 1000);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Sync search from URL (?q=...) e.g. header global search
    const location = useLocation();
    const urlQuery = new URLSearchParams(location.search).get("q") || "";
    useEffect(() => {
        if (urlQuery) {
            setSearchTerm(urlQuery);
            setSearchQuery(urlQuery); // apply immediately, skip debounce
        }
    }, [urlQuery]);


    const fetchGames = async (cursor = null) => {
        try {
            if (!cursor) {
                setLoading(true);
            } else {
                setLoadingMore(true);
            }

            const queryParams = new URLSearchParams({
                limit: 20,
                ...fetchParams // Spread initial params like isLiveGame: true
            });

            if (cursor) queryParams.append('cursor', cursor);

            // If we have a fixed game type (like 'crash'), use it.
            // Otherwise, use activeNav unless it's 'all'.
            if (fixedGameType) {
                queryParams.append('gameType', fixedGameType);
            } else if (activeNav !== 'all') {
                queryParams.append('gameType', activeNav);
            }

            // Exclude Game Types
            if (excludeGameTypes) {
                queryParams.append('excludeGameTypes', excludeGameTypes);
            }

            if (searchQuery) queryParams.append('name', searchQuery);
            if (selectedProviders.length > 0) queryParams.append('providers', selectedProviders.join(','));

            const gamesRes = await api.get(`/games?${queryParams.toString()}`);

            if (gamesRes.data.success) {
                if (cursor) {
                    setGames(prev => [...prev, ...gamesRes.data.data]);
                } else {
                    setGames(gamesRes.data.data);
                }

                setNextCursor(gamesRes.data.pagination.nextCursor);

                // Update Game Types logic
                // Only if showGameTypeFilter is true and on initial load (no cursor)
                if (showGameTypeFilter && gamesRes.data.gameTypes && activeNav === 'all' && !cursor) {
                    const rawTypes = gamesRes.data.gameTypes;
                    // Handle both old format (array of strings) and new format (array of objects {id, count})
                    const types = rawTypes.map(item => {
                        const id = typeof item === 'object' ? item.id : item;
                        const count = typeof item === 'object' ? item.count : 0;
                        return {
                            id: id,
                            label: id === 'uncategorized' ? 'UNCATEGORIZED' : id.replace(/-/g, ' ').toUpperCase(),
                            count: count
                        };
                    });

                    const totalGamesCount = gamesRes.data.pagination.total;
                    setGameTypes([{ id: 'all', label: 'ALL GENRES', count: totalGamesCount }, ...types]);
                }

                // Providers logic removed from here to separate useEffect
            }
        } catch (error) {
            console.error("Error fetching games data:", error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    // Initial Fetch & Filter Changes
    useEffect(() => {
        setNextCursor(null);
        fetchGames(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeNav, selectedProviders, searchQuery, fixedGameType, excludeGameType, JSON.stringify(excludeGameTypes)]);

    const handleLoadMore = () => {
        if (nextCursor) {
            fetchGames(nextCursor);
        }
    };

    // Infinite Scroll Observer
    const lastGameElementRef = (node) => {
        if (loadingMore) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && nextCursor) {
                handleLoadMore();
            }
        });

        if (node) observer.current.observe(node);
    };

    // Helper function to get image URL
    const getImageUrl = (img) => {
        if (!img) return null;
        return img;
    };

    const scroll = (ref, scrollOffset) => {
        if (ref.current) {
            ref.current.scrollLeft += scrollOffset;
        }
    };

    // Toggle Provider Selection
    const toggleProvider = (providerId) => {
        setActiveNav("all");
        setSelectedProviders(prev => {
            if (prev.includes(providerId)) {
                return prev.filter(id => id !== providerId);
            } else {
                return [...prev, providerId];
            }
        });
    };

    // Initial Provider Fetch (Once per mount)
    useEffect(() => {
        const fetchProviders = async () => {
            try {
                // Fetch all providers for the slider irrespective of game type filters
                // Or create a dedicated endpoint. reusing games endpoint for now but 
                // ideally this should be a separate /providers call if we want ALL providers.
                // Assuming we want providers relevant to the current page type (e.g. slots providers):

                const res = await api.get('/games', {
                    params: {
                        ...fetchParams,
                        limit: 1 // Minimal fetch just to get facets if backend supports it, or use dedicated
                    }
                });

                if (res.data.success && res.data.gameProviders) {
                    const providerList = res.data.gameProviders.map(p => ({
                        id: p._id,
                        label: p.name,
                        logo: p.logo,
                        slug: p.slug
                    }));
                    setProviders(providerList);
                }
            } catch (err) {
                console.error("Error fetching providers", err);
            }
        };
        fetchProviders();
    }, []); // Empty dependency array = run once on mount

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (providerRef.current && !providerRef.current.contains(event.target)) {
                setIsProviderDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const NavSkeleton = () => (
        <div className="casino-nav-bar" style={{ opacity: 0.6 }}>
            <div className="casino-nav-scroll-area">
                {[1, 2, 3, 4, 5, 6].map(i => (
                    <div key={i} className="skeleton-nav-item skeleton-loading" />
                ))}
            </div>
        </div>
    );

    const GameCardSkeleton = () => (
        <div className="casino-games-grid">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                <div key={i} className="skeleton-game-card skeleton-loading" />
            ))}
        </div>
    );

    return (
        <div className="casino-page">
            {/* Banner Section */}
            <div className="casino-banner-section">
                <img
                    src={bannerImage}
                    alt={`${pageTitle} Banner`}
                    className="casino-banner-image"
                />
                <div className="casino-banner-overlay">
                    <h2>{bannerTitle || pageTitle}</h2>
                </div>
            </div>

            {/* Top Provider Slider (Redirects) */}
            {providers.length > 0 && (
                <div className="casino-slider-container" style={{ marginTop: '20px', marginBottom: '20px' }}>
                    <div className="casino-nav-scroll-area" ref={providerScrollRef}>
                        {providers.map((item) => (
                            <button
                                key={item.id}
                                className="casino-provider-item"
                                onClick={() => navigate(`/providers/${item.slug || item.label.toLowerCase().replace(/ /g, '-')}`)}
                            >
                                {item.logo ? (
                                    <img src={getImageUrl(item.logo)} alt={item.label} className="provider-logo" />
                                ) : (
                                    <span className="provider-text">{item.label}</span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Search Bar */}
            <div className="casino-search-section w-full">
                <div className="casino-search-container">
                    <div className="casino-search-input-wrapper">
                        <FaSearch className="casino-search-icon" />
                        <input
                            type="text"
                            placeholder="Search games..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="casino-search-input"
                        />
                    </div>

                    {/* Custom Multi-Select Dropdown */}
                    <div className="casino-custom-dropdown" ref={providerRef} style={{ position: 'relative' }}>
                        <button
                            className="casino-dropdown-toggle"
                            onClick={() => setIsProviderDropdownOpen(!isProviderDropdownOpen)}
                        >
                            <span>
                                {selectedProviders.length === 0
                                    ? "Select Providers"
                                    : `${selectedProviders.length} Provider${selectedProviders.length > 1 ? 's' : ''} Selected`}
                            </span>
                            <FaChevronDown style={{ marginLeft: '10px', fontSize: '12px' }} />
                        </button>

                        {isProviderDropdownOpen && (
                            <div
                                className="casino-dropdown-menu"
                                style={{
                                    position: 'absolute',
                                    top: '100%',
                                    right: 0,
                                    marginTop: '10px',
                                    backgroundColor: '#1a1a1a',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    borderRadius: '12px',
                                    padding: '10px',
                                    width: '250px',
                                    maxHeight: '300px',
                                    overflowY: 'auto',
                                    zIndex: 9999,
                                    boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                                }}
                            >
                                {providers.map((provider) => {
                                    const isSelected = selectedProviders.includes(provider.id);
                                    return (
                                        <div
                                            key={provider.id}
                                            onClick={() => toggleProvider(provider.id)}
                                            style={{
                                                padding: '10px 12px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                cursor: 'pointer',
                                                borderRadius: '8px',
                                                marginBottom: '4px',
                                                backgroundColor: isSelected ? 'rgba(255, 153, 0, 0.15)' : 'transparent',
                                                color: isSelected ? '#ff9900' : '#ccc',
                                                transition: 'all 0.2s ease',
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isSelected) {
                                                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                                                    e.currentTarget.style.color = '#fff';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isSelected) {
                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                    e.currentTarget.style.color = '#ccc';
                                                }
                                            }}
                                        >
                                            <span style={{ fontSize: '13px', fontWeight: isSelected ? '600' : '400' }}>{provider.label}</span>
                                            {isSelected && <FaCheck style={{ color: '#ff9900', fontSize: '10px' }} />}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Content Section */}
            <div className="casino-content-section container-fluid">
                {/* Game Type Slider (Navigation) */}
                {showGameTypeFilter && (
                    <div className="casino-slider-container">
                        {loading && gameTypes.length === 0 ? (
                            <NavSkeleton />
                        ) : (
                            <div className="casino-nav-bar">
                                <button
                                    className="nav-scroll-btn left"
                                    onClick={() => scroll(navRef, -200)}
                                    aria-label="Scroll left"
                                >
                                    <FaChevronLeft />
                                </button>

                                <div className="casino-nav-scroll-area" ref={navRef}>
                                    {gameTypes.map((item) => (
                                        <button
                                            key={item.id}
                                            className={`casino-nav-item ${activeNav === item.id ? "active" : ""
                                                }`}
                                            onClick={() => setActiveNav(item.id)}
                                        >
                                            <span className="nav-icon">{getIconForType(item.id)}</span>
                                            <div className="nav-info">
                                                <span className="nav-label">{item.label}</span>
                                                <span className="nav-count" style={{ fontSize: '0.7em', opacity: 0.7 }}>{item.count} games</span>
                                            </div>
                                        </button>
                                    ))}
                                </div>

                                <button
                                    className="nav-scroll-btn right"
                                    onClick={() => scroll(navRef, 200)}
                                    aria-label="Scroll right"
                                >
                                    <FaChevronRight />
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Games Grid */}
                <div className="casino-games-container" ref={gamesContainerRef}>
                    {loading ? (
                        <GameCardSkeleton />
                    ) : games.length === 0 ? (
                        <div className="no-games-found">
                            <p>No games found matching your criteria.</p>
                        </div>
                    ) : (
                        <div className="casino-games-grid">
                            {games.map((game) => (
                                <GameCard
                                    key={game._id}
                                    game={game}
                                    onClick={undefined}
                                />
                            ))}
                        </div>
                    )}

                    {/* Infinite Scroll Loader / Sentinel */}
                    {(nextCursor || loadingMore) && (
                        <div
                            ref={lastGameElementRef}
                            style={{ display: 'flex', justifyContent: 'center', marginTop: '30px', paddingBottom: '30px', height: '50px' }}
                        >
                            {loadingMore && (
                                <div className="loading-spinner"></div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GamePageTemplate;
